import itiriri from 'itiriri';
import {
  ApplyPatchReq,
  HSDoseRound,
  PatchApplicationActivityDto,
  PatchApplicationDto, PatchDrugAdministrationDto, ReapplyPatchReq, WardDto
} from 'server-openapi';
import {PersistentQueue, QueueEntry, QueueOperationException} from '../core/queue/PersistentQueue';
import {Entry, IStorage} from '../core/storage/Contract';
import { SyncStreamAPI } from './api';
import {IFacilityGroupSyncService } from './SyncCenter';
import { v4 as uuidv4 } from 'uuid';
import { SyncUtils } from './utils/SyncUtils';
import { Logger } from '../core/logger/logger';
import { DateUtils } from '../core/utils/dateUtils';
import { startOfDay, subDays } from 'date-fns';
import {MemoryCache} from "../core/storage/MemoryCache";
import {RoundOp} from "./SyncRounds";
import {mergePatchApplications} from "./utils/PatchApplicationUtils";

export type PatchApplicationOp = ApplyPatchApplicationOp | ManagePatchApplicationOp | ReapplyPatchApplicationOp;

export interface PatchApplicationBaseOp extends QueueEntry {
  type: string;
  patchApplication: PatchApplicationDto;
  activity: PatchApplicationActivityDto;
}

export interface ApplyPatchApplicationOp extends PatchApplicationBaseOp {
  type: 'apply-patch';
  patchDrugAdministration: PatchDrugAdministrationDto;
  doseRoundClinicalSystemId: string;
}
export interface ReapplyPatchApplicationOp extends PatchApplicationBaseOp {
  type: 'reapply-patch';
  patchDrugAdministration: PatchDrugAdministrationDto;
  doseRoundClinicalSystemId: string;
}
export interface ManagePatchApplicationOp extends PatchApplicationBaseOp {
  type: 'manage-patch';
}


const logger = new Logger('SyncPatchApplications');

export class SyncPatchApplications implements IFacilityGroupSyncService {
  private async checkDoseRound(roundStore: IStorage<HSDoseRound>, clinicalSystemId: string | undefined) {
    if (clinicalSystemId) {
      const round = await roundStore.get(clinicalSystemId);
      var found = false;
      if (round === undefined) {
        for await (const delivery of this.roundQueue.iterate()) {
          if (delivery.value.type === "round-create") {
            const createRoundOp = delivery.value;
            if (createRoundOp.clinicalSystemId === clinicalSystemId) {
              found = true;
              break;
            }
          }
        }
        if (!found) {
          throw new QueueOperationException("UnrecoverableError", `Round '${clinicalSystemId}' not found when creating patch application activity`);
        }
        else {
          throw new QueueOperationException("PendingParent", `Dose round ${clinicalSystemId} has not been uploaded yet`);
        }
      }
      else if (!round.hsId || round.hsId <= 0) {
        // Not uploaded yet.
        throw new QueueOperationException("PendingParent", `Dose round ${clinicalSystemId} has not been uploaded yet`);
      }
      return round;
    }
    return undefined;
  }
  private async checkAdHoc(round: HSDoseRound | undefined, adhoc: string | undefined) {
    if (!!adhoc) {
      const adHocDrug = round!.administeredDoses?.flatMap(x => x.administeredAdHocDrugs).find(x => x?.clinicalSystemId == adhoc);
      if (adHocDrug) {
        return adHocDrug;
      }
      var found = false;
      for await (const delivery of this.roundQueue.iterate()) {
        if (delivery.value.type === "adhoc-drug-create") {
          const createOp = delivery.value;
          if (createOp.clinicalSystemId === adhoc) {
            found = true;
            break;
          }
        }
      }
      if (!found) {
        throw new QueueOperationException("UnrecoverableError", `Unable to find administered ad-hoc drug with clinical system id: ${adhoc} for dose round ${round!.hsId}`);
      }
      throw new QueueOperationException("PendingParent", `Administered ad-hoc drug ${adhoc} has not been uploaded yet`);
    }
  }
  private async checkAdministeredDrug(round: HSDoseRound | undefined, administeredDrug: string | undefined) {
    if (!!administeredDrug) {
      const adrug = round!.administeredDoses?.flatMap(x => x.administeredDrugs).find(x => x?.clinicalSystemId == administeredDrug);
      if (adrug) {
        return adrug;
      }
      var found = false;
      for await (const delivery of this.roundQueue.iterate()) {
        if (delivery.value.type === "drug-create") {
          const createOp = delivery.value;
          if (createOp.clinicalSystemId === administeredDrug) {
            found = true;
            break;
          }
        }
      }
      if (!found) {
        throw new QueueOperationException("UnrecoverableError", `Unable to find administered drug with clinical system id: ${administeredDrug} for dose round ${round!.hsId}`);
      }
      throw new QueueOperationException("PendingParent", `Administered drug ${adrug} has not been uploaded yet`);
    }
  }


  get name(): string {
    return 'SyncPatchApplications';
  }
  private async isStale(p: PatchApplicationDto): Promise<boolean> {
    return (!!(!p.active && p.clientUpdatedAt && p.clientUpdatedAt < DateUtils.fromDate(subDays(startOfDay(new Date()), 14))));
  }

  private async isFacilityGroup(p: PatchApplicationDto, facilityGroupId: string): Promise<boolean> {
    if (!p.wardId) {
      return false;
    }
    const facilityGroup = await this.facilitiesStore.get(p.wardId!.toString());
    return facilityGroup?.facilityGroupId?.toString() == facilityGroupId;
  }

  constructor(
    private api: SyncStreamAPI,
    private storage: MemoryCache<PatchApplicationDto>,
    private facilitiesStore: IStorage<WardDto>,
    private latestChangeNumbers: IStorage<number | undefined>,
    private roundsStore: IStorage<HSDoseRound>,
    private queue: PersistentQueue<PatchApplicationOp>,
    private roundQueue: PersistentQueue<RoundOp>,
  ) { 
    
  }
  async load(facilityGroupId: string): Promise<void> {
    await this.storage.reset(async (r) => {
          return await this.isFacilityGroup(r, facilityGroupId);
    });
  }

  async syncDown(facilityGroupId?: string) {
    const facilitiesToSync = await SyncUtils.getFacilitiesForGroup(facilityGroupId, this.facilitiesStore);
    const changeNumber = await SyncUtils.getChangeNumberForFacilities(
      facilitiesToSync.map((x) => x.hsId!),
      this.latestChangeNumbers,
    );

    const patchApplicationData = await this.syncFacilityDown(
      facilityGroupId!,
      changeNumber,
    );

    const mergedResult = await Promise.all(patchApplicationData
        .map(async (run) => {
          const key = this.storage.get_key!(run);
          const existing = await this.storage.get(key);
          if (existing) {
            return mergePatchApplications(run, existing);
          }
          return run;
        }));
    await this.storage.setMany(mergedResult.map((run) => {
        const key = this.storage.get_key!(run);
        return {
          key: key!,
          value: {
            ...run,
            clinicalSystemId: key,
          },
        };
      }),
    );
        

    await SyncUtils.setChangeNumberForFacilities(
        facilitiesToSync.map((x) => x.hsId!)!,
        this.latestChangeNumbers,
        patchApplicationData
    );
  }

  async syncFacilityDown(facilityGroupId: string, changeNumber: number): Promise<PatchApplicationDto[]> {
    const pageSize = 200;
    //changeNumber = isFinite(changeNumber) ? changeNumber : 0;
    const patchApplications =
      await this.api.patchApplications.patchApplicationsListPatchApplications(
        changeNumber,
        parseInt(facilityGroupId),
        pageSize
      );

    if (patchApplications.data.length === pageSize) {
      return [
        ...patchApplications.data,
        ...(await this.syncFacilityDown(facilityGroupId, SyncUtils.getLatestChangeNumberForV2Data(patchApplications.data)!)),
      ];
    }
    return patchApplications.data;
  }

  enqueue = {
    applyPatch: async (patchApplication: PatchApplicationDto, patchDrugAdministration: PatchDrugAdministrationDto, startActivity: PatchApplicationActivityDto, doseRoundClinicalSystemId: string): Promise<ApplyPatchApplicationOp> => {
      
      // The clinical system id of the patch application  is the same as that of its start activity.
      patchApplication.clinicalSystemId ??= uuidv4();
      startActivity.clinicalSystemId = patchApplication.clinicalSystemId;
      startActivity.patchApplicationClinicalSystemId = startActivity.clinicalSystemId;
      patchApplication.active = true;
      const initialApplication = {...patchApplication };
      patchApplication.activities = [ startActivity ];
      patchApplication.patchDrugAdministrations = [patchDrugAdministration];
      const mergedEntry = mergePatchApplications(initialApplication, patchApplication);
      const result: ApplyPatchApplicationOp = {
        type: "apply-patch",
        state: "Pending",
        patchApplication: mergedEntry,
        activity: startActivity,
        doseRoundClinicalSystemId: doseRoundClinicalSystemId,
        patchDrugAdministration: patchDrugAdministration,
      };
      
      await this.storage.set(mergedEntry.clinicalSystemId!, mergedEntry);
      await this.queue.unshift(result);
      return result;
    },
    reapplyPatch: async (patchApplication: PatchApplicationDto, patchDrugAdministration: PatchDrugAdministrationDto, restartActivity: PatchApplicationActivityDto, doseRoundClinicalSystemId: string): Promise<ReapplyPatchApplicationOp> => {
      // The clinical system id of the patch application is the same as that of its start activity.
      restartActivity.clinicalSystemId ??= uuidv4();
      restartActivity.patchApplicationClinicalSystemId = patchApplication.clinicalSystemId;
      const result: ReapplyPatchApplicationOp = {
        type: "reapply-patch",
        state: "Pending",
        activity: restartActivity,
        patchApplication: patchApplication,
        doseRoundClinicalSystemId: doseRoundClinicalSystemId,
        patchDrugAdministration: patchDrugAdministration
      };
      var newPatchApplication: PatchApplicationDto = {
        activities: [restartActivity],
        patchDrugAdministrations: [patchDrugAdministration]
      };
      const mergedEntry = mergePatchApplications(patchApplication, newPatchApplication);
      await this.storage.set(patchApplication.clinicalSystemId!, mergedEntry);
      await this.queue.unshift(result);
      return result;
    },
    managePatch: async (patchApplication: PatchApplicationDto, activity: PatchApplicationActivityDto): Promise<ManagePatchApplicationOp> => {
      activity.clinicalSystemId ??= uuidv4();
      activity.patchApplicationClinicalSystemId = patchApplication.clinicalSystemId;
      const result: ManagePatchApplicationOp = {
        type: "manage-patch",
        state: "Pending",
        activity: activity, 
        patchApplication: patchApplication
      };
      // merge the operation into the patch application object.
      var newPatchApplication: PatchApplicationDto = {
        activities: [activity]
      };
      const mergedEntry = mergePatchApplications(patchApplication, newPatchApplication);
      await this.storage.set(patchApplication.clinicalSystemId!, mergedEntry);
      await this.queue.unshift(result);
      return result;
    },
  };

  //eslint-disable-next-line sonarjs/cognitive-complexity, max-lines-per-function
  async syncUp() {
    const entries = new Map<string, PatchApplicationDto>();
    for await (const operation of this.queue.iterate()) {
      try {
        switch (operation.value.type) {
        case 'apply-patch': {
            const storedApplication = (await this.storage.get(operation.value.patchApplication.clinicalSystemId!)) ?? entries.get(operation.value.patchApplication.clinicalSystemId!);
            if (!storedApplication) {
              throw new QueueOperationException("UnrecoverableError", `Patch application '${operation.value.patchApplication.clinicalSystemId}' not found when attempting to upload patch application`);
            }
            
            const startActivity = operation.value.activity;
            const round = await this.checkDoseRound(this.roundsStore, operation.value.doseRoundClinicalSystemId);
            const adhoc = operation.value.patchDrugAdministration.administeredAdHocDrugClinicalSystemId ?? undefined;

            const ahdr = await this.checkAdHoc(round, adhoc);
            operation.value.patchDrugAdministration.administeredAdHocDrugId = ahdr?.hsId;
            
            const administeredDrug = operation.value.patchDrugAdministration.administeredDrugClinicalSystemId ?? undefined;
            const dr = await this.checkAdministeredDrug(round, administeredDrug);
            operation.value.patchDrugAdministration.administeredDrugId = dr?.hsId;

            if (!adhoc && !administeredDrug) {
              throw new QueueOperationException("UnrecoverableError", "An administered drug or administered ad-hoc drug must be specified");
            }
  
            const request : ApplyPatchReq = {
              patchDrugAdministration: operation.value.patchDrugAdministration ?? {},
              patientId: operation.value.patchApplication.patientId ?? -1,
              applicationActivity: startActivity
            };

            var response = await this.api.patchApplications.patchApplicationsCreatePatchApplication(request);
            entries.set(operation.value.patchApplication.clinicalSystemId!, mergePatchApplications(response.data, storedApplication!))
          }
          break;

          case 'reapply-patch': {
            const storedApplication =
                (await this.storage.get(operation.value.activity.patchApplicationClinicalSystemId!)) ?? entries.get(operation.value.activity.patchApplicationClinicalSystemId!);
            if (!storedApplication?.hsId || storedApplication.hsId <= 0) {
              throw new QueueOperationException("PendingParent", `Patch application  ${storedApplication?.clinicalSystemId} has not been uploaded yet`);
            }
            const activity = operation.value.activity;
            activity.patchApplicationId = storedApplication.hsId;
            const round = await this.checkDoseRound(this.roundsStore, operation.value.doseRoundClinicalSystemId);
            const adhoc = operation.value.patchDrugAdministration.administeredAdHocDrugClinicalSystemId ?? undefined;
            const ahdr = await this.checkAdHoc(round, adhoc);
            operation.value.patchDrugAdministration.administeredAdHocDrugId = ahdr?.hsId;

            const administeredDrug = operation.value.patchDrugAdministration.administeredDrugClinicalSystemId ?? undefined;
            const dr = await this.checkAdministeredDrug(round, administeredDrug);
            operation.value.patchDrugAdministration.administeredDrugId = dr?.hsId;

            if (!adhoc && !administeredDrug) {
              throw new QueueOperationException("UnrecoverableError", "An administered drug or administered ad-hoc drug must be specified");
            }

            const reapplyReq : ReapplyPatchReq = {
              patchDrugAdministration: operation.value.patchDrugAdministration ?? {},
              reapplicationActivity: activity,
              patchApplicationId: storedApplication.hsId,
            };
            var response = await this.api.patchApplications.patchApplicationsReapplyPatch(reapplyReq);
            entries.set(operation.value.patchApplication.clinicalSystemId!, mergePatchApplications(response.data, storedApplication!))
          }
          break;
            
          case 'manage-patch': {
            const storedApplication =
                (await this.storage.get(operation.value.activity.patchApplicationClinicalSystemId!)) ?? entries.get(operation.value.activity.patchApplicationClinicalSystemId!);
            if (!storedApplication?.hsId || storedApplication.hsId <= 0) {
              throw new QueueOperationException("PendingParent", `Patch application  ${storedApplication?.clinicalSystemId} has not been uploaded yet`);
            }
            const activity = operation.value.activity;
            if (!activity) {
              // TODO:  Make this unrecoverable
              throw new Error(`Activity '${operation.value.activity.clinicalSystemId}' unable to be found for patch application`);
            }
            activity.patchApplicationId = storedApplication.hsId;
            var response = await this.api.patchApplications.patchApplicationsManagePatchApplication(activity);
            entries.set(storedApplication.clinicalSystemId!, mergePatchApplications(response.data, storedApplication!));
          }
        }
        await operation.complete();
      } catch (error) {
        await operation.handleException(logger, error, operation);
      }
    }
    await this.storage.setMany(
      itiriri(entries.entries())
        .map((entry) => ({ key: entry[0], value: entry[1] }))
        .toArray(),
    );
  }

  async clear() {
    await this.storage.clear();
    await this.latestChangeNumbers.clear();
    await this.queue.clear();
  }

  hasQueuedData() {
    return this.queue.hasPendingMessages();
  }

  queuedDataCount() {
    return this.queue.pendingMessagesCount();
  }

  isAllowed(canUserAccessMedication: boolean): boolean {
    // Only if you can view a round.
    return canUserAccessMedication;
  }
  async archive(deletedPatientIds: string[]): Promise<void> {
    const keysToDelete: string[] = [];
    for (let [k, v] of (await this.storage.all()).entries()) {
      if (await this.isStale(v) || deletedPatientIds.some(x => x === v.patientId?.toString() ?? "-1")) {
        keysToDelete.push(k)
      }
    }
    await this.storage.deleteMany(keysToDelete);
  }
  setEncryptionVersion(version: number): void {
    this.storage.compressOnSave = (version > 1);
  }
  async rewrite(): Promise<void> {
    const entries: Entry<PatchApplicationDto>[] = [...(await this.storage.all())].map((keyValueArray) => {
      return {
        key: keyValueArray[0],
        value: keyValueArray[1]
      };
    });

    return await this.storage.setMany(entries);
  }}
